<template>
  <div class="card campaign-preview-card">
    <div class="card-header">
      <h5 class="mb-0">Campaign Preview</h5>
    </div>

    <div class="card-body p-0">
      <div class="campaign-preview-container">
        <div class="campaign-dialog">
          <div class="campaign-card">

            <!-- Campaign Image -->
            <div class="campaign-image" :style="imageStyle">

              <!-- Fallback Image -->
              <div
                  v-if="!campaign.media_item_url"
                  class="campaign-fallback"
                  :style="{
                  background:
                    campaign.type === 'Donation' || campaign.type === 'donation'
                      ? 'linear-gradient(135deg, #FF8A3D 0%, #F72F00 100%)'
                      : 'linear-gradient(135deg, #6F7CFF 0%, #F72F00 100%)',
                }"
              >
                <span>{{ getFallbackIcon }}</span>
              </div>

              <!-- Image Gradient -->
              <div class="campaign-gradient"/>

              <!-- Close Button -->
              <button
                  type="button"
                  class="close-button"
                  aria-label="Close campaign"
              >
                ×
              </button>
            </div>

            <!-- Campaign Content -->
            <div class="campaign-content">

              <!-- Title -->
              <h2 class="campaign-title" :style="{ color: campaignColor }">
                {{ campaign.greeting ? campaign.greeting + ' user name' : 'Campaign Title' }}
              </h2>

              <!-- Description -->
              <p class="campaign-description">
                {{ campaign.description || 'Campaign description' }}
              </p>


              <div class="target-progress" v-if="amountIsSet()">
                <div class="header-row">
                  <div
                      class="icon-wrap"
                      :style="{
        backgroundColor: `${campaignColor}1A`,
        color: campaignColor
      }"
                  >
                    <span>⚑</span>
                  </div>

                  <div class="title-wrap">
                    <span class="title">Almost there!</span>
                  </div>
                </div>

                <div class="amount-copy">
                  <span>Just </span>

                  <strong :style="{ color: campaignColor }">
                    {{ amountLeftText }}
                  </strong>

                  <span> more to reach our target</span>
                </div>

                <div class="progress-bar-wrap">
                  <div
                      class="progress-bar"
                      :style="{
        width: `${progressValue * 100}%`,
        backgroundColor: campaignColor
      }"
                  />
                </div>

                <div class="footer-row">
    <span
        class="funded"
        :style="{ color: campaignColor }"
    >
      {{ percentText }}
    </span>

                  <span class="target">
      {{ targetText }}
    </span>
                </div>
              </div>

              <!-- Action Button -->
              <button
                  v-if="campaign.action_text"
                  type="button"
                  class="campaign-action"
                  :style="{
                  backgroundColor: campaignColor,
                  color: '#fff',
                }"
              >
                {{ campaign.action_text }}
              </button>

              <!-- Footer -->
              <div class="campaign-footer">
                <span
                    class="campaign-footer-icon"
                    :style="{ color: campaignColor }"
                >
                  {{ getTypeIcon }}
                </span>

                <p>
                  {{
                    campaign.message ||
                    'Your support creates a healthier tomorrow.'
                  }}
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted} from 'vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const normalizedColor = (hex) => {
  const value = (hex || '#F72F00').trim();

  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) {
    return value;
  }

  return '#F72F00';
};

const isDonationType = computed(() => {
  return (
      props.campaign?.type === 'Donation' ||
      props.campaign?.type === 'donation'
  );
});

function amountIsSet() {
  // return false;
  const amount = props.campaign?.amount;
  return amount != null && amount !== '';
}



const campaignColor = computed(() =>
    normalizedColor(
        props.campaign?.color?.color_code ||
        props.campaign?.color_code ||
        props.campaign?.primaryColor
    )
);

const imageStyle = computed(() => {
  if (!props.campaign?.media_item_url) {
    return {};
  }

  return {
    backgroundImage: `url("${props.campaign.media_item_url}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
});

const getTypeIcon = computed(() => {
  return isDonationType.value ? '♥' : '◎';
});

const getFallbackIcon = computed(() => {
  return isDonationType.value ? '❤' : '⚑';
});

/*
|--------------------------------------------------------------------------
| Donation Progress
|--------------------------------------------------------------------------
*/

const targetAmount = computed(() => {
  return Number(props.campaign?.amount ?? 0);
});

const fundedPercent = computed(() => {
  if (targetAmount.value <= 0) {
    return 0;
  }

  return Math.min(
      (Number(props.campaign?.amount_raised ?? 0) / targetAmount.value) * 100,
      100
  );
});

/*
 * Percentage of the amount that is still left.
 */
const remainingPercent = computed(() => {
  if (targetAmount.value <= 0) {
    return 0;
  }

  return Math.max(100 - fundedPercent.value, 0);
});

const progressValue = computed(() =>
    Math.min(Math.max(fundedPercent.value / 100, 0), 1)
);

/*
|--------------------------------------------------------------------------
| Currency / Money Formatting
|--------------------------------------------------------------------------
*/

const currencyCode = computed(() => {
  return (
      props.campaign?.currency_code ||
      props.campaign?.currency ||
      'NGN'
  );
});

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currencyCode.value,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);
};

const amountLeftText = computed(() => {
  const target = Number(props.campaign?.amount ?? 0);
  const raised = Number( props.campaign?.amount_raised ?? 0);

  // Ensure we don't return a negative amount left
  const left = Math.max(target - raised, 0);

  return formatMoney(left);
});

const targetText = computed(() => {
  return `Target: ${formatMoney(targetAmount.value)}`;
});

const percentText = computed(() => {
  return `${Math.round(fundedPercent.value)}% funded`;
});
</script>

<style scoped>
:global(body) {
  font-family: Inter, "Segoe UI", sans-serif;
}

/*
|--------------------------------------------------------------------------
| Preview Wrapper
|--------------------------------------------------------------------------
*/

.campaign-preview-card {
  position: sticky;
  top: 20px;
}

.campaign-preview-container {
  padding: 24px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.campaign-dialog {
  width: 100%;
  max-width: 420px;
}

/*
|--------------------------------------------------------------------------
| Campaign Card
|--------------------------------------------------------------------------
*/

.campaign-card {
  background: #ffffff;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

/*
|--------------------------------------------------------------------------
| Campaign Image
|--------------------------------------------------------------------------
*/

.campaign-image {
  position: relative;
  width: 100%;
  height: 230px;
  overflow: hidden;

  border-bottom-left-radius: 500px 120px;
  border-bottom-right-radius: 500px 100px;
}  

.campaign-fallback {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  font-size: 72px;
}

.campaign-gradient {
  position: absolute;
  inset: auto 0 0 0;

  height: 80px;

  background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.25),
      transparent
  );
}

/*
|--------------------------------------------------------------------------
| Close Button
|--------------------------------------------------------------------------
*/

.close-button {
  position: absolute;
  top: 14px;
  right: 14px;

  width: 36px;
  height: 36px;

  border: none;
  border-radius: 50%;

  background: rgba(255, 255, 255, 0.8);
  color: #171717;

  font-size: 24px;
  line-height: 1;

  display: grid;
  place-items: center;

  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

  cursor: default;
}

/*
|--------------------------------------------------------------------------
| Content
|--------------------------------------------------------------------------
*/

.campaign-content {
  padding: 22px 24px 20px;
}

.campaign-title {
  margin: 0;

  font-size: 25px;
  font-weight: 700;
  line-height: 1.2;

  letter-spacing: -0.02em;
}

.campaign-description {
  margin: 12px 0 0;

  color: #111111;

  font-size: 16px;
  line-height: 1.45;
  font-weight: 400;
}

/*
|--------------------------------------------------------------------------
| Action Button
|--------------------------------------------------------------------------
*/

.campaign-action {
  width: 100%;

  border: none;
  border-radius: 14px;

  padding: 14px 16px;
  margin-top: 22px;

  font-size: 16px;
  font-weight: 600;

  cursor: default;

  transition: opacity 0.2s ease;
}

.campaign-action:hover {
  opacity: 0.94;
}

/*
|--------------------------------------------------------------------------
| Footer
|--------------------------------------------------------------------------
*/

.campaign-footer {
  display: flex;
  align-items: flex-start;

  gap: 8px;
  margin-top: 16px;
}

.campaign-footer-icon {
  font-size: 18px;
  line-height: 1;

  margin-top: 2px;
}

.campaign-footer p {
  margin: 0;

  color: #666666;

  font-size: 12px;
  line-height: 1.3;
}

/*
|--------------------------------------------------------------------------
| Target Bar
|--------------------------------------------------------------------------
*/


.target-progress {
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  background: rgba(28, 88, 122, 0.06);
  border: 1px solid rgba(28, 88, 122, 0.15);
  box-sizing: border-box;
  margin-top: 12px;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
}

.title-wrap {
  flex: 1;
}

.title {
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.amount-copy {
  margin-top: 5px;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.65);
}

.amount-copy strong {
  font-weight: 800;
}

.progress-bar-wrap {
  margin-top: 8px;
  height: 7px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(28, 88, 122, 0.12);
}

.progress-bar {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.footer-row {
  margin-top: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 12px;
}

.funded {
  font-weight: 600;
}

.target {
  color: rgba(0, 0, 0, 0.5);
}


/*
|--------------------------------------------------------------------------
| Responsive
|--------------------------------------------------------------------------
*/

@media (max-width: 480px) {
  .campaign-preview-container {
    padding: 16px;
  }

  .campaign-content {
    padding-left: 18px;
    padding-right: 18px;
  }

  .campaign-title {
    font-size: 22px;
  }
}


</style>
